/**
 * Sends FTUX events to ows-uxo, one POST per event, fire and forget (momentum-online ows_uxo/docs/EVENTS.md). fetch,
 * never the global axios: its 5 s POST cache would swallow a repeated event, and its 401 handling would sign the
 * owner out. Credentials stay at their default (include fails the CORS check). Nothing here may break the page:
 * every failure is swallowed, with a console.debug outside production builds.
 */
import { uxoEndpoint } from './endpoint';
import type { UxoClientEvent } from './envelope';

/** The owner's owners-hub access token, the one Owner Hub's /auth/check takes. */
const ACCESS_TOKEN_KEY = 'access_token';

/**
 * The answers worth a resend, which the eventId makes safe: no answer at all (a network error), 500 (ows-uxo's
 * MongoDB failed) and 503 (Owner Hub could not say whose the token is). Owner Hub answers a token it does not know
 * with a 500, so ows-uxo answers it a 503 on every try: two resends at most, never a loop. Every other answer
 * (400, 401, 403, 413, 415) is final.
 */
const RESEND_STATUSES: readonly number[] = [500, 503];
const MAX_RESENDS = 2;
/** The wait before the first resend; the next one waits twice as long (2 s, then 4 s). */
const RESEND_DELAY_MS = 2000;

const debug = (...details: unknown[]): void => {
  if (process.env.NODE_ENV !== 'production') console.debug('[ftux events]', ...details);
};

/** Read at each try: a refreshed token is used, and a signed-out owner sends nothing more. */
const readToken = (): string | null => {
  try {
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    // Storage blocked: no token, so no event.
    return null;
  }
};

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** One try: the answer's status, or null when none came (offline, a network error, a CORS refusal). */
const post = async (url: string, token: string, body: string): Promise<number | null> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body,
    });
    return response.status;
  } catch (error) {
    debug('no answer', error);
    return null;
  }
};

/** The first try, then a resend after no answer, a 500 or a 503, MAX_RESENDS times at most. */
const deliver = async (url: string, body: string): Promise<void> => {
  for (let attempt = 0; attempt <= MAX_RESENDS; attempt += 1) {
    if (attempt > 0) await wait(RESEND_DELAY_MS * 2 ** (attempt - 1));
    const token = readToken();
    if (!token) return;
    const status = await post(url, token, body);
    if (status !== null && !RESEND_STATUSES.includes(status)) {
      if (status >= 300) debug('refused', status);
      return;
    }
  }
  debug('dropped after its resends');
};

/** Sends `event` when this page has an events URL and the owner a token. Returns at once and never throws. */
export const sendUxo = (event: UxoClientEvent): void => {
  const url = uxoEndpoint();
  if (!url) return;
  deliver(url, JSON.stringify({ events: [event] })).catch((error: unknown) => debug('not sent', error));
};
